import { EventCategory, MessageEvent, MessageType } from '@lendi/analytics-web';

import { lendiAnalytics } from './analytics';
import { eventTracking } from './base';

enum MissingMessageType {
  'GENERIC' = 'Generic',
}
type CompleteMessageType = MissingMessageType | MessageType;
interface ModifiedMessageEvent extends Omit<MessageEvent, 'messageType'> {
  messageType: CompleteMessageType;
  applicationId?: string;
}

const baseMessageEventTracking =
  (messageType: CompleteMessageType) =>
  (
    analytics: ReturnType<typeof lendiAnalytics>,
    props: Pick<ModifiedMessageEvent, 'purpose' | 'value' | 'applicationId'>
  ) => {
    eventTracking<ModifiedMessageEvent>(analytics, {
      ...props,
      messageType,
      event_name: 'Message Displayed',
      category: EventCategory.FUNNELS,
    });
  };

/**
 * A function that recursively traverse an arbitary-depth object
 * and return all lodash style path to all leaves
 * @param target the node to traverse
 * @param parentPath parent path to carry over
 * @param result result array
 * @returns the result array which is an one dimensional array
 */
export const getPathAndValue = (
  target: { [key: string]: any } | any[] | string,
  parentPath: string = '',
  result: string[] = []
): string[] => {
  if (target instanceof Array) {
    // handle array, remove empty Item
    target.forEach((child, idx) => {
      if (child) {
        result = getPathAndValue(child, `${parentPath}[${idx}]`, result);
      }
    });
  } else if (typeof target === 'object') {
    // handle Object, ignore empty Object
    if (Object.keys(target).length !== 0) {
      for (const [key, value] of Object.entries(target)) {
        // generate the new path
        const newPath = `${parentPath}${parentPath ? '.' : ''}${key}`;
        // recursively call it with child
        result = getPathAndValue(value, newPath, result);
      }
    }
  } else {
    // handle primitive value, string in this case
    result = result.concat([`${parentPath}&${target}`]);
  }
  return result;
};

const bulkErrorMessagesTracking =
  (analytics: ReturnType<typeof lendiAnalytics>, applicationId?: string) =>
  (validationErrors?: { [key: string]: any }) => {
    if (!validationErrors) {
      return;
    }
    const errorMessageTracking = baseMessageEventTracking(MessageType.ERROR);
    // one more safe guard to wrap the recursion in try catch block
    try {
      const pathValues = getPathAndValue(validationErrors);
      pathValues.forEach((pathValue) => {
        const [path, value] = pathValue.split('&');
        errorMessageTracking(analytics, { purpose: path, value: value, applicationId: applicationId });
      });
    } catch (err) {
      throw new Error('bulkErrorMessagesTracking recursion errors out, details: ' + JSON.stringify(err));
    }
  };

export const messageEventTracking = (analytics: ReturnType<typeof lendiAnalytics>) => ({
  error: baseMessageEventTracking(MessageType.ERROR).bind(null, analytics),
  generic: baseMessageEventTracking(MissingMessageType.GENERIC).bind(null, analytics),
  bulkError: bulkErrorMessagesTracking.bind(null, analytics),
});
